import React, { useState, useMemo, useEffect } from "react"
import { styled, createTheme, ThemeProvider } from "@mui/material/styles"
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Grid,
  TextField,
  MenuItem,
  Card,
  CardContent,
  Stack,
  Tooltip,
  IconButton,
  Button,
  CircularProgress,
  Slider,
  LinearProgress,
  Alert,
  Divider,
} from "@mui/material"
import { PieChart } from "@mui/x-charts/PieChart"
import { BarChart } from "@mui/x-charts/BarChart"
import {
  HowToVote,
  ReportProblem,
  TableChart,
  TravelExplore,
  Groups,
  Leaderboard,
  WarningAmber,
  Download as DownloadIcon,
  Print as PrintIcon,
  Map as MapIcon,
  Refresh as RefreshIcon,
  TrackChanges,
  OnlinePrediction,
  Campaign,
  Psychology,
  Info as InfoIcon,
  ThumbsUpDown,
} from "@mui/icons-material"
import dayjs from "dayjs"

import Header from "../../layouts/Header"
import BackIcon from "../../components/BackIcon"
import CustomBackdrop from "../../components/CustomBackdrop"
import CustomAggregationSegregationComponent from "../../components/CustomAggregationSegregationComponent"
import { useQuery, api, showWarning } from "../../api"

const StyledRoot = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  marginTop: theme.spacing(3),
  flexGrow: 1,
  padding: "0 16px",
  paddingBottom: "40px",
}))

const theme = createTheme({
  palette: { primary: { main: "#196C87" }, secondary: { main: "#2E7D32" }, error: { main: "#d32f2f" } },
})

const KPICard = ({ title, value, icon, color, subtitle, strategyText }) => (
  <Card elevation={2} sx={{ height: "100%", borderLeft: `6px solid ${color}`, borderRadius: 2 }}>
    <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start">
        <Box>
          <Box display="flex" alignItems="center">
            <Typography
              variant="body2"
              color="textSecondary"
              fontWeight="bold"
              sx={{ textTransform: "uppercase", mb: 1 }}
            >
              {title}
            </Typography>
            {strategyText && (
              <Tooltip title={<Typography fontSize={13}>{strategyText}</Typography>} placement="top" arrow>
                <IconButton size="small" sx={{ ml: 0.5, mt: -1, p: 0 }}>
                  <InfoIcon fontSize="small" color="primary" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
          <Typography variant="h4" fontWeight="bold" sx={{ color }}>
            {value}
          </Typography>
          {subtitle && (
            <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: "block", fontWeight: "500" }}>
              {subtitle}
            </Typography>
          )}
        </Box>
        <Box sx={{ bgcolor: `${color}1A`, p: 1.5, borderRadius: "50%", display: "flex" }}>
          {React.cloneElement(icon, { sx: { color, fontSize: 32 } })}
        </Box>
      </Box>
    </CardContent>
  </Card>
)

const EncuestaMantaIndex = () => {
  const [activeTab, setActiveTab] = useState(0)
  const [filtroParroquia, setFiltroParroquia] = useState("TODAS")

  // ESTADOS DEL SIMULADOR PREDICTIVO
  const [simCanton, setSimCanton] = useState("TODAS")
  const [simCandidato, setSimCandidato] = useState("")
  const [simRecall, setSimRecall] = useState(0)
  const [simAntivoto, setSimAntivoto] = useState(0)
  const [simVotoFirme, setSimVotoFirme] = useState(0)
  const [simCanal, setSimCanal] = useState("Redes Sociales")

  // =========================================================================
  // CONSULTA AL BACKEND CON EXTRACCIÓN DEFENSIVA
  // =========================================================================
  const {
    data: reportes,
    isLoading,
    isFetching,
    refetch,
  } = useQuery({
    queryKey: ["getResultadosEncuestaManta", filtroParroquia],
    queryFn: async () => {
      const payload = {
        encuesta: "ENA2600000101",
        parroquia: filtroParroquia === "TODAS" ? null : filtroParroquia,
      }
      try {
        const res = await api.post("/EjecEncuestas/getAnaliticaManta", payload)

        let rawData = res?.data || {}
        if (rawData.data) rawData = rawData.data
        if (rawData.data) rawData = rawData.data

        return {
          kpi1: Array.isArray(rawData?.kpi1) ? rawData.kpi1 : [],
          kpi2: Array.isArray(rawData?.kpi2) ? rawData.kpi2 : [],
          kpi3: Array.isArray(rawData?.kpi3) ? rawData.kpi3 : [],
          fase2: rawData?.fase2_simulador || {},
        }
      } catch (error) {
        console.error("Error consultando la analítica:", error)
        return { kpi1: [], kpi2: [], kpi3: [], fase2: {} }
      }
    },
    refetchOnWindowFocus: false,
  })

  const data = reportes || { kpi1: [], kpi2: [], kpi3: [], fase2: {} }
  const fase2 = data.fase2 || {}
  const mixMediosData = fase2.mixMedios || []
  const firmezaData = fase2.firmezaVoto || []

  // Extraer lista dinámica de candidatos para el combo del simulador
  const listaCandidatos = useMemo(() => {
    const candidatosUnicos = new Set()
    data.kpi1.forEach((row) => candidatosUnicos.add(row.Intencion_Alcalde))
    return Array.from(candidatosUnicos).filter(
      (c) =>
        !c.toUpperCase().includes("NULO") && !c.toUpperCase().includes("BLANCO") && !c.toUpperCase().includes("SABE"),
    )
  }, [data.kpi1])

  useEffect(() => {
    if (listaCandidatos.length > 0 && !simCandidato) {
      setSimCandidato(listaCandidatos[0])
    }
  }, [listaCandidatos, simCandidato])

  // =========================================================================
  // MOTOR REACTIVO: ACTUALIZAR SLIDERS SEGÚN EL CANDIDATO SELECCIONADO
  // =========================================================================
  useEffect(() => {
    if (simCandidato && data.kpi1 && data.kpi1.length > 0) {
      // 1. Calcular Voto Firme base (Intención de voto actual del candidato)
      let votosCandidato = 0
      let votosTotales = 0
      data.kpi1.forEach((row) => {
        votosTotales += Number(row.Cantidad_Votos || 0)
        if (row.Intencion_Alcalde === simCandidato) {
          votosCandidato += Number(row.Cantidad_Votos || 0)
        }
      })
      const pctFirme = votosTotales > 0 ? (votosCandidato / votosTotales) * 100 : 0
      setSimVotoFirme(Math.round(pctFirme))

      // 2. Extraer el Antivoto real de la base de datos para ese candidato (Pregunta 17)
      if (fase2.antivotoCandidatos) {
        const anti = fase2.antivotoCandidatos.find((c) => c.candidato === simCandidato)
        setSimAntivoto(anti ? Math.round(anti.valor) : 0)
      }

      // 3. Tasa de Recall general (Penetración)
      if (fase2.tasaPenetracion !== undefined) setSimRecall(Math.round(fase2.tasaPenetracion))
    }
  }, [simCandidato, data.kpi1, fase2])

  // =========================================================================
  // FÓRMULAS PREDICTIVAS EN TIEMPO REAL
  // =========================================================================
  const techoElectoral = Math.max(0, 100 - simAntivoto)
  const persuabilidad = Math.max(0, 100 - simVotoFirme)

  let factorCanal = 1.0
  if (simCanal === "Recorridos de Calle") factorCanal = 1.3
  if (simCanal === "Boca a Boca") factorCanal = 1.2
  if (simCanal === "Redes Sociales") factorCanal = 1.0
  if (simCanal === "Radio Local") factorCanal = 0.8
  const eficienciaCampana = Math.min(100, Math.round(simRecall * factorCanal))

  const addId = (rows) => {
    if (!Array.isArray(rows)) return []
    return rows.map((r, i) => ({ ...r, id: r.id || i }))
  }

  // =========================================================================
  // EXPORTACIÓN A PDF Y CSV
  // =========================================================================
  const handleExportarPDF = () => {
    let exportData = []
    let cols = []
    let title = ""

    if (activeTab === 0) {
      exportData = data.kpi1
      cols = [
        { header: "Parroquia", key: "Parroquia" },
        { header: "Sexo", key: "Sexo" },
        { header: "Rango Edad", key: "Rango_Edad" },
        { header: "Candidato a Alcalde", key: "Intencion_Alcalde" },
        { header: "Votos", key: "Cantidad_Votos" },
        { header: "% Segmento", key: "Pct_Segmento" },
      ]
      title = "Detalle Intención de Voto"
    } else if (activeTab === 1) {
      exportData = data.kpi2
      cols = [
        { header: "Parroquia", key: "Parroquia" },
        { header: "Sexo", key: "Sexo" },
        { header: "Rango Edad", key: "Rango_Edad" },
        { header: "Problema Principal", key: "Problema_Principal" },
        { header: "Menciones", key: "Menciones" },
        { header: "Score", key: "Score_Criticidad" },
      ]
      title = "Detalle Dolores Ciudadanos"
    } else if (activeTab === 2) {
      exportData = data.kpi3
      cols = [
        { header: "Nº", key: "No_Pregunta" },
        { header: "Pregunta", key: "Pregunta" },
        { header: "Parroquia", key: "Parroquia" },
        { header: "Sexo", key: "Sexo" },
        { header: "Rango Edad", key: "Rango_Edad" },
        { header: "Respuesta Obtenida", key: "Respuesta" },
        { header: "Frecuencia", key: "Total_Respuestas" },
        { header: "% Relativo", key: "Pct_Relativo_Segmento" },
      ]
      title = "Matriz Global Transversal (Cubo)"
    } else {
      return showWarning("No hay tabla disponible en esta vista para imprimir.")
    }

    if (!exportData || exportData.length === 0) return showWarning("No hay datos tabulados para generar el PDF.")

    const printWindow = window.open("", "_blank")
    const htmlContent = `
      <html>
        <head>
          <title>Reporte - ${title}</title>
          <style>
            body { font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif; padding: 20px; color: #333; }
            h2 { text-align: center; color: #196C87; margin-bottom: 5px; }
            h4 { text-align: center; color: #666; margin-top: 0; margin-bottom: 20px; }
            table { width: 100%; border-collapse: collapse; font-size: 12px; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #f5f7fa; color: #196C87; font-weight: bold; }
            tr:nth-child(even) { background-color: #f9f9f9; }
            .footer { margin-top: 30px; font-size: 10px; color: #999; text-align: right; }
          </style>
        </head>
        <body>
          <h2>Estudio de Valoración Social - Manta</h2>
          <h4>${title} | Filtro: ${filtroParroquia}</h4>
          <table>
            <thead><tr>${cols.map((c) => `<th>${c.header}</th>`).join("")}</tr></thead>
            <tbody>${exportData.map((row) => `<tr>${cols.map((c) => `<td>${row[c.key] !== undefined && row[c.key] !== null ? row[c.key] : ""}</td>`).join("")}</tr>`).join("")}</tbody>
          </table>
          <div class="footer">Generado por SIAC Web Analytics - ${dayjs().format("DD/MM/YYYY HH:mm")}</div>
          <script>window.onload = function() { window.print(); setTimeout(function(){ window.close(); }, 500); }</script>
        </body>
      </html>
    `
    printWindow.document.write(htmlContent)
    printWindow.document.close()
  }

  const handleExportarCSV = () => {
    let exportData = []
    let cols = []
    let filename = ""

    if (activeTab === 0) {
      exportData = data.kpi1
      cols = [
        { header: "Parroquia", key: "Parroquia" },
        { header: "Sexo", key: "Sexo" },
        { header: "Rango Edad", key: "Rango_Edad" },
        { header: "Candidato a Alcalde", key: "Intencion_Alcalde" },
        { header: "Votos (Frecuencia)", key: "Cantidad_Votos" },
        { header: "% en Segmento", key: "Pct_Segmento" },
      ]
      filename = `Intencion_Voto_Manta_${dayjs().format("YYYYMMDD_HHmm")}.csv`
    } else if (activeTab === 1) {
      exportData = data.kpi2
      cols = [
        { header: "Parroquia", key: "Parroquia" },
        { header: "Sexo", key: "Sexo" },
        { header: "Rango Edad", key: "Rango_Edad" },
        { header: "Problema Principal", key: "Problema_Principal" },
        { header: "Menciones", key: "Menciones" },
        { header: "Score Criticidad", key: "Score_Criticidad" },
      ]
      filename = `Dolores_Ciudadanos_Manta_${dayjs().format("YYYYMMDD_HHmm")}.csv`
    } else if (activeTab === 2) {
      exportData = data.kpi3
      cols = [
        { header: "Nº Pregunta", key: "No_Pregunta" },
        { header: "Pregunta", key: "Pregunta" },
        { header: "Parroquia", key: "Parroquia" },
        { header: "Sexo", key: "Sexo" },
        { header: "Rango Edad", key: "Rango_Edad" },
        { header: "Respuesta Obtenida", key: "Respuesta" },
        { header: "Frecuencia", key: "Total_Respuestas" },
        { header: "% Relativo", key: "Pct_Relativo_Segmento" },
      ]
      filename = `Matriz_Global_Manta_${dayjs().format("YYYYMMDD_HHmm")}.csv`
    } else {
      return showWarning("No hay datos en esta vista para exportar.")
    }

    if (!exportData || exportData.length === 0) return showWarning("No hay datos en esta vista para exportar.")

    const headerRow = cols.map((c) => `"${c.header}"`).join(",")
    const rows = exportData.map((row) =>
      cols.map((c) => `"${row[c.key] !== undefined && row[c.key] !== null ? row[c.key] : ""}"`).join(","),
    )

    const csvContent = "\uFEFF" + [headerRow, ...rows].join("\n")
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" })
    const url = URL.createObjectURL(blob)
    const link = document.createElement("a")
    link.href = url
    link.setAttribute("download", filename)
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  // =========================================================================
  // PROCESAMIENTO DE DATOS PARA GRÁFICOS Y TARJETAS KPI FASE 1
  // =========================================================================
  const metricasGenerales = useMemo(() => {
    let totalEncuestas = 0
    const intencionMap = {}
    const problemasMap = {}

    if (data.kpi1 && data.kpi1.length > 0) {
      data.kpi1.forEach((row) => {
        totalEncuestas += Number(row.Cantidad_Votos || 0)
        intencionMap[row.Intencion_Alcalde] =
          (intencionMap[row.Intencion_Alcalde] || 0) + Number(row.Cantidad_Votos || 0)
      })
    }

    if (data.kpi2 && data.kpi2.length > 0) {
      data.kpi2.forEach((row) => {
        problemasMap[row.Problema_Principal] =
          (problemasMap[row.Problema_Principal] || 0) + Number(row.Score_Criticidad || 0)
      })
    }

    const intencionArr = Object.entries(intencionMap)
      .map(([k, v]) => ({ label: k, value: v }))
      .sort((a, b) => b.value - a.value)
    const problemasArr = Object.entries(problemasMap)
      .map(([k, v]) => ({ label: k, value: v }))
      .sort((a, b) => b.value - a.value)

    const liderIntencion = intencionArr.length > 0 ? intencionArr[0].label : "N/A"
    const topProblema = problemasArr.length > 0 ? problemasArr[0].label : "N/A"

    return { totalEncuestas, intencionArr, problemasArr, liderIntencion, topProblema }
  }, [data])

  return (
    <ThemeProvider theme={theme}>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <BackIcon />

        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", mb: 1, color: "#196C87" }}>
          <TravelExplore sx={{ fontSize: 36, mr: 1 }} />
          <Typography variant="h4" fontWeight="bold">
            Estudio de Valoración Social - Manta
          </Typography>
        </Box>
        <Typography variant="body1" align="center" color="textSecondary" mb={3}>
          Dashboard Analítico y Cuadros de Mando Transaccionales
        </Typography>

        <CustomBackdrop isLoading={isLoading} />

        <StyledRoot>
          {/* FILTROS GLOBALES */}
          <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 2, borderTop: "4px solid #196C87" }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={4}>
                <TextField
                  select
                  fullWidth
                  label="Filtrar por Parroquia"
                  value={filtroParroquia}
                  onChange={(e) => setFiltroParroquia(e.target.value)}
                  size="small"
                >
                  <MenuItem value="TODAS">Consolidado General (Todas)</MenuItem>
                  <MenuItem value="Tarqui">Tarqui</MenuItem>
                  <MenuItem value="Manta">Manta</MenuItem>
                  <MenuItem value="Los Esteros">Los Esteros</MenuItem>
                  <MenuItem value="Eloy Alfaro">Eloy Alfaro</MenuItem>
                  <MenuItem value="UrbiManta">UrbiManta</MenuItem>
                  <MenuItem value="San Mateo">San Mateo</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={8}>
                <Box display="flex" justifyContent={{ xs: "flex-start", sm: "flex-end" }} alignItems="center">
                  {isFetching && !isLoading && <CircularProgress size={16} sx={{ mr: 1.5, color: "#196C87" }} />}
                  <Typography variant="body2" color="textSecondary" sx={{ mr: 2 }}>
                    Mostrando resultados demográficos consolidados de la campaña.
                  </Typography>
                  <Tooltip title="Recargar datos ahora">
                    <span>
                      <IconButton
                        onClick={() => refetch()}
                        color="primary"
                        disabled={isFetching || isLoading}
                        size="small"
                        sx={{ border: "1px solid #196C87", bgcolor: "white" }}
                      >
                        <RefreshIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
              </Grid>
            </Grid>
          </Paper>

          {/* TARJETAS KPI DE RESUMEN EJECUTIVO */}
          <Grid container spacing={3} mb={4}>
            <Grid item xs={12} sm={4}>
              <KPICard
                title="Muestra Tabulada"
                value={metricasGenerales.totalEncuestas}
                icon={<Groups />}
                color="#196C87"
                subtitle={`Filtro aplicado: ${filtroParroquia}`}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <KPICard
                title="NPS Político"
                value={`${(fase2.npsPolitico || 0) > 0 ? "+" : ""}${fase2.npsPolitico || 0}`}
                icon={<ThumbsUpDown />}
                color={(fase2.npsPolitico || 0) >= 0 ? "#2E7D32" : "#d32f2f"}
                subtitle="Fórmula: % Promotores - % Detractores"
                strategyText="Calculado cruzando las Preguntas 6 a 10 de la encuesta de Manta (Gestión del Gobierno, Prefecto, Alcaldesa, Concejales). Evalúa el índice neto de favorabilidad a las autoridades vigentes. Si es negativo, limpie imagen antes de proponer. Si es positivo, ataque y proponga."
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <KPICard
                title="Desgaste de Gestión"
                value={`${fase2.desgasteOficialismo || 0}%`}
                icon={<WarningAmber />}
                color={(fase2.desgasteOficialismo || 0) > 55 ? "#ed6c02" : "#196C87"}
                subtitle="Fórmula: % Total de Detractores"
                strategyText="Calculado tomando el % de personas que calificaron como 'Mala' o 'Muy Mala' a las autoridades en las Preguntas 6 a 10. Indica nivel de rechazo al sistema. > 55% indica que la campaña debe enfocarse en la palabra 'CAMBIO'."
              />
            </Grid>
          </Grid>

          {/* TABS DE NAVEGACIÓN PRINCIPAL */}
          <Paper elevation={1} sx={{ mb: 2, borderRadius: 2 }}>
            <Tabs
              value={activeTab}
              onChange={(e, newValue) => setActiveTab(newValue)}
              indicatorColor="primary"
              textColor="primary"
              variant="scrollable"
              scrollButtons="auto"
              sx={{ borderBottom: 1, borderColor: "divider" }}
            >
              <Tab icon={<HowToVote />} label="DETALLE INTENCIÓN DE VOTO" sx={{ fontWeight: "bold" }} />
              <Tab icon={<ReportProblem />} label="DETALLE DOLORES CIUDADANOS" sx={{ fontWeight: "bold" }} />
              <Tab icon={<TableChart />} label="MATRIZ GLOBAL (CUBO)" sx={{ fontWeight: "bold" }} />
              <Tab icon={<MapIcon />} label="MAPA DE CALOR (GEO-ESTRATÉGICO)" sx={{ fontWeight: "bold" }} />
              <Tab icon={<TrackChanges />} label="SIMULADOR PREDICTIVO" sx={{ fontWeight: "bold", color: "#ed6c02" }} />
            </Tabs>
          </Paper>

          {/* CONTENEDOR DE TABLAS Y SIMULADOR */}
          <Paper
            elevation={3}
            sx={{
              width: "100%",
              borderRadius: 2,
              p: 2,
              minHeight: 400,
              bgcolor: activeTab === 4 ? "transparent" : "white",
              boxShadow: activeTab === 4 ? 0 : 3,
            }}
          >
            {/* Solo mostramos la barra de exportación en los tabs de tablas (0, 1, 2) */}
            {(activeTab === 0 || activeTab === 1 || activeTab === 2) && (
              <>
                {/* GRÁFICOS ANALÍTICOS RÁPIDOS EN LAS PESTAÑAS 0 Y 1 */}
                {activeTab === 0 && (
                  <Paper elevation={0} sx={{ p: 2, mb: 3, border: "1px solid #e0e0e0", borderRadius: 2 }}>
                    <Typography variant="h6" color="primary" fontWeight="bold" align="center" mb={1}>
                      Termómetro: Intención de Voto (Consolidado)
                    </Typography>
                    <Box sx={{ display: "flex", justifyContent: "center", minHeight: 350 }}>
                      {metricasGenerales.intencionArr.length > 0 ? (
                        <PieChart
                          series={[
                            {
                              data: metricasGenerales.intencionArr.map((d, i) => ({
                                id: i,
                                label: d.label,
                                value: d.value,
                              })),
                              innerRadius: 50,
                              paddingAngle: 2,
                              cornerRadius: 4,
                              valueFormatter: (v) =>
                                `${v.value} Votos (${((v.value / metricasGenerales.totalEncuestas) * 100).toFixed(1)}%)`,
                            },
                          ]}
                          height={300}
                          slotProps={{
                            legend: {
                              padding: 40,
                              direction: "column",
                              position: { vertical: "middle", horizontal: "right" },
                              itemMarkWidth: 15,
                              itemMarkHeight: 15,
                              labelStyle: { fontSize: 13, textOverflow: "ellipsis" },
                            },
                          }}
                          margin={{ right: 250 }}
                        />
                      ) : (
                        <Typography color="textSecondary" fontStyle="italic">
                          Sin datos tabulados.
                        </Typography>
                      )}
                    </Box>
                  </Paper>
                )}

                {activeTab === 1 && (
                  <Paper elevation={0} sx={{ p: 2, mb: 3, border: "1px solid #e0e0e0", borderRadius: 2 }}>
                    <Typography variant="h6" color="error" fontWeight="bold" align="center" mb={1}>
                      Score de Dolores Ciudadanos
                    </Typography>
                    <Box sx={{ minHeight: 350 }}>
                      {metricasGenerales.problemasArr.length > 0 ? (
                        <BarChart
                          dataset={metricasGenerales.problemasArr.slice(0, 8)}
                          yAxis={[{ scaleType: "band", dataKey: "label" }]}
                          series={[{ dataKey: "value", color: "#ed6c02" }]}
                          layout="horizontal"
                          height={350}
                          margin={{ left: 220, right: 30, top: 10, bottom: 20 }}
                        />
                      ) : (
                        <Typography color="textSecondary" fontStyle="italic">
                          Sin datos tabulados.
                        </Typography>
                      )}
                    </Box>
                  </Paper>
                )}

                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="subtitle1" fontWeight="bold" color="textSecondary">
                    Listado Detallado (Tabla de Contingencia)
                  </Typography>
                  <Box>
                    <Button
                      variant="outlined"
                      color="primary"
                      startIcon={<PrintIcon />}
                      onClick={handleExportarPDF}
                      sx={{ fontWeight: "bold", bgcolor: "white", mr: 2 }}
                    >
                      Guardar / Imprimir PDF
                    </Button>
                    <Button
                      variant="contained"
                      color="secondary"
                      startIcon={<DownloadIcon />}
                      onClick={handleExportarCSV}
                      sx={{ fontWeight: "bold" }}
                    >
                      Exportar a CSV
                    </Button>
                  </Box>
                </Stack>
              </>
            )}

            {/* TABLA 1 */}
            {activeTab === 0 && data.kpi1.length > 0 && (
              <CustomAggregationSegregationComponent
                data={addId(data.kpi1)}
                columnsConfig={({ totalSumVotos }) => [
                  { accessorKey: "Parroquia", header: "Parroquia", size: 180 },
                  { accessorKey: "Sexo", header: "Sexo", size: 140 },
                  { accessorKey: "Rango_Edad", header: "Rango Edad", size: 150 },
                  { accessorKey: "Intencion_Alcalde", header: "Candidato a Alcalde", size: 300 },
                  {
                    accessorKey: "Cantidad_Votos",
                    header: "Votos (Frecuencia)",
                    size: 180,
                    accessorFn: (row) => Number(row.Cantidad_Votos) || 0,
                    aggregationFn: "sum",
                    AggregatedCell: ({ cell }) => (
                      <Box color="primary.main" fontWeight="bold">
                        {cell.getValue().toLocaleString()}
                      </Box>
                    ),
                    Footer: () => (
                      <Stack>
                        Total Votos:
                        <Box color="primary.main" fontWeight="bold">
                          {Number(totalSumVotos).toLocaleString()}
                        </Box>
                      </Stack>
                    ),
                  },
                  {
                    accessorKey: "Pct_Segmento",
                    header: "% en Segmento",
                    size: 180,
                    Cell: ({ cell }) => `${cell.getValue()}%`,
                  },
                ]}
                aggregations={{
                  totalSumVotos: (data) => data.reduce((acc, curr) => acc + Number(curr.Cantidad_Votos || 0), 0),
                }}
                tableOptions={{ initialState: { sorting: [{ id: "Cantidad_Votos", desc: true }] } }}
              />
            )}

            {/* TABLA 2 */}
            {activeTab === 1 && data.kpi2.length > 0 && (
              <CustomAggregationSegregationComponent
                data={addId(data.kpi2)}
                columnsConfig={({ totalSumMenciones, totalSumScore }) => [
                  { accessorKey: "Parroquia", header: "Parroquia", size: 180 },
                  { accessorKey: "Sexo", header: "Sexo", size: 140 },
                  { accessorKey: "Rango_Edad", header: "Rango Edad", size: 150 },
                  { accessorKey: "Problema_Principal", header: "Problema Principal", size: 350 },
                  {
                    accessorKey: "Menciones",
                    header: "Menciones",
                    size: 150,
                    accessorFn: (row) => Number(row.Menciones) || 0,
                    aggregationFn: "sum",
                    AggregatedCell: ({ cell }) => (
                      <Box color="error.main" fontWeight="bold">
                        {cell.getValue().toLocaleString()}
                      </Box>
                    ),
                    Footer: () => (
                      <Stack>
                        Total Menciones:
                        <Box color="error.main" fontWeight="bold">
                          {Number(totalSumMenciones).toLocaleString()}
                        </Box>
                      </Stack>
                    ),
                  },
                  {
                    accessorKey: "Score_Criticidad",
                    header: "Score (Criticidad)",
                    size: 180,
                    accessorFn: (row) => Number(row.Score_Criticidad) || 0,
                    aggregationFn: "sum",
                    AggregatedCell: ({ cell }) => (
                      <Box color="warning.main" fontWeight="bold">
                        {cell.getValue().toLocaleString()}
                      </Box>
                    ),
                    Footer: () => (
                      <Stack>
                        Total Score:
                        <Box color="warning.main" fontWeight="bold">
                          {Number(totalSumScore).toLocaleString()}
                        </Box>
                      </Stack>
                    ),
                  },
                ]}
                aggregations={{
                  totalSumMenciones: (data) => data.reduce((acc, curr) => acc + Number(curr.Menciones || 0), 0),
                  totalSumScore: (data) => data.reduce((acc, curr) => acc + Number(curr.Score_Criticidad || 0), 0),
                }}
                tableOptions={{ initialState: { sorting: [{ id: "Score_Criticidad", desc: true }] } }}
              />
            )}

            {/* TABLA 3 */}
            {activeTab === 2 && data.kpi3.length > 0 && (
              <CustomAggregationSegregationComponent
                data={addId(data.kpi3)}
                columnsConfig={({ totalSumFrecuencia }) => [
                  { accessorKey: "No_Pregunta", header: "Nº", size: 100 },
                  { accessorKey: "Pregunta", header: "Pregunta", size: 450 },
                  { accessorKey: "Parroquia", header: "Parroquia", size: 180 },
                  { accessorKey: "Sexo", header: "Sexo", size: 140 },
                  { accessorKey: "Rango_Edad", header: "Rango Edad", size: 150 },
                  { accessorKey: "Respuesta", header: "Respuesta Obtenida", size: 350 },
                  {
                    accessorKey: "Total_Respuestas",
                    header: "Frecuencia",
                    size: 150,
                    accessorFn: (row) => Number(row.Total_Respuestas) || 0,
                    aggregationFn: "sum",
                    AggregatedCell: ({ cell }) => (
                      <Box color="primary.main" fontWeight="bold">
                        {cell.getValue().toLocaleString()}
                      </Box>
                    ),
                    Footer: () => (
                      <Stack>
                        Total Frecuencia:
                        <Box color="primary.main" fontWeight="bold">
                          {Number(totalSumFrecuencia).toLocaleString()}
                        </Box>
                      </Stack>
                    ),
                  },
                  {
                    accessorKey: "Pct_Relativo_Segmento",
                    header: "% Relativo",
                    size: 160,
                    Cell: ({ cell }) => `${cell.getValue()}%`,
                  },
                ]}
                aggregations={{
                  totalSumFrecuencia: (data) => data.reduce((acc, curr) => acc + Number(curr.Total_Respuestas || 0), 0),
                }}
                tableOptions={{ initialState: { sorting: [{ id: "No_Pregunta", desc: false }] } }}
              />
            )}

            {/* PESTAÑA 4: MAPA LOOKER STUDIO */}
            {activeTab === 3 && (
              <Box sx={{ display: "flex", flexDirection: "column", height: 600 }}>
                <Box mb={2}>
                  <Typography variant="h6" color="primary" fontWeight="bold">
                    Mapa de Control Territorial y Geolocalización
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    <b>Instrucciones:</b> Para visualizar el mapa de calor interactivo, ingresa a tu cuenta de{" "}
                    <b>Looker Studio</b>, conecta la vista SQL proporcionada en el Backend, genera un informe usando{" "}
                    <b>Google Maps</b> y obtén el enlace de incrustación <i>(Archivo &gt; Insertar informe)</i>. Luego
                    reemplaza el <code>src</code> en el código de este componente.
                  </Typography>
                </Box>
                <iframe
                  title="Mapa Calor Looker Studio"
                  width="100%"
                  height="100%"
                  src="https://datastudio.google.com/embed/reporting/703a8e31-5294-4891-bba8-e1c1d3c482b4/page/6TyAG"
                  frameBorder="0"
                  style={{ border: 0, borderRadius: "8px", flexGrow: 1, backgroundColor: "#f5f7fa" }}
                  allowFullScreen
                  sandbox="allow-storage-access-by-user-activation allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox"
                ></iframe>
              </Box>
            )}

            {/* PESTAÑA 5: MÓDULO DE SIMULACIÓN PREDICTIVA (FASE 2) */}
            {activeTab === 4 && (
              <Box>
                <Grid container spacing={3} mb={4}>
                  <Grid item xs={12} md={6}>
                    <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
                      <Box display="flex" alignItems="center" mb={1}>
                        <Typography variant="subtitle1" fontWeight="bold" color="primary">
                          Share of Voice (Mix de Medios)
                        </Typography>
                        <Tooltip
                          title="Pregunta 4: Medios utilizados para informarse. Mide por qué canales se entera el electorado de Manta."
                          placement="top"
                        >
                          <IconButton size="small">
                            <InfoIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                      <Box sx={{ height: 350, display: "flex", justifyContent: "center", alignItems: "center" }}>
                        {mixMediosData.length > 0 ? (
                          <BarChart
                            dataset={mixMediosData}
                            yAxis={[{ scaleType: "band", dataKey: "label" }]}
                            series={[{ dataKey: "value", color: "#196C87", valueFormatter: (v) => `${v}%` }]}
                            layout="horizontal"
                            height={320}
                            margin={{ left: 160, right: 30, top: 20, bottom: 20 }}
                          />
                        ) : (
                          <Typography color="textSecondary">No hay datos suficientes</Typography>
                        )}
                      </Box>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
                      <Box display="flex" alignItems="center" mb={1}>
                        <Typography variant="subtitle1" fontWeight="bold" color="primary">
                          Índice de Firmeza de Voto
                        </Typography>
                        <Tooltip
                          title="Cruce de Preguntas 16 (Indecisos/Nulos) vs Voto Directo. Representa Voto Duro vs Blando."
                          placement="top"
                        >
                          <IconButton size="small">
                            <InfoIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                      <Box sx={{ height: 350 }}>
                        {firmezaData.length > 0 ? (
                          <BarChart
                            dataset={firmezaData}
                            xAxis={[{ scaleType: "band", dataKey: "label" }]}
                            series={[{ dataKey: "value", color: "#2E7D32", valueFormatter: (v) => `${v}%` }]}
                            height={320}
                          />
                        ) : (
                          <Typography color="textSecondary">No hay datos suficientes</Typography>
                        )}
                      </Box>
                    </Paper>
                  </Grid>
                </Grid>

                <Divider sx={{ my: 4 }}>
                  <Typography
                    variant="h6"
                    color="textSecondary"
                    fontWeight="bold"
                    sx={{ display: "flex", alignItems: "center" }}
                  >
                    <OnlinePrediction sx={{ mr: 1 }} /> Módulo de Simulación Predictiva Territorial
                  </Typography>
                </Divider>

                <Grid container spacing={4}>
                  <Grid item xs={12} md={5}>
                    <Paper
                      elevation={3}
                      sx={{ p: 4, borderRadius: 3, bgcolor: "white", borderTop: "5px solid #ed6c02" }}
                    >
                      <Typography variant="h6" color="primary" fontWeight="bold" gutterBottom>
                        Controles de Simulación
                      </Typography>
                      <Box mb={3}>
                        <TextField
                          select
                          fullWidth
                          label="Territorio Activo"
                          value={simCanton}
                          onChange={(e) => setSimCanton(e.target.value)}
                          size="small"
                          sx={{ mb: 3 }}
                        >
                          <MenuItem value="TODAS">Consolidado General Manta</MenuItem>
                          <MenuItem value="Tarqui">Parroquia Tarqui</MenuItem>
                          <MenuItem value="Manta">Parroquia Manta</MenuItem>
                          <MenuItem value="Los Esteros">Parroquia Los Esteros</MenuItem>
                          <MenuItem value="Eloy Alfaro">Parroquia Eloy Alfaro</MenuItem>
                          <MenuItem value="UrbiManta">Parroquia UrbiManta</MenuItem>
                          <MenuItem value="San Mateo">Parroquia San Mateo</MenuItem>
                        </TextField>

                        <TextField
                          select
                          fullWidth
                          label="Candidato a Simular"
                          value={simCandidato}
                          onChange={(e) => setSimCandidato(e.target.value)}
                          size="small"
                          sx={{ mb: 3 }}
                        >
                          {listaCandidatos.map((c, i) => (
                            <MenuItem key={i} value={c}>
                              {c}
                            </MenuItem>
                          ))}
                        </TextField>

                        <TextField
                          select
                          fullWidth
                          label="Canal Dominante de la Semana"
                          value={simCanal}
                          onChange={(e) => setSimCanal(e.target.value)}
                          size="small"
                          sx={{ mb: 1 }}
                        >
                          <MenuItem value="Redes Sociales">Redes Sociales (Base)</MenuItem>
                          <MenuItem value="Recorridos de Calle">Recorridos de Calle (Alto Impacto)</MenuItem>
                          <MenuItem value="Boca a Boca">Boca a Boca (Orgánico)</MenuItem>
                          <MenuItem value="Radio Local">Radio Local (Tradicional)</MenuItem>
                        </TextField>
                        <Typography variant="caption" color="textSecondary">
                          Seleccione dónde invertirá el mayor presupuesto esta semana.
                        </Typography>
                      </Box>
                      <Box mb={3}>
                        <Typography variant="body2" fontWeight="bold" gutterBottom>
                          Recordación de Mensaje (Recall): {simRecall}%
                        </Typography>
                        <Slider
                          value={simRecall}
                          onChange={(e, val) => setSimRecall(val)}
                          valueLabelDisplay="auto"
                          color="primary"
                        />
                        <Typography variant="caption" color="textSecondary">
                          Partiendo de Base Analizada: <b>{fase2.tasaPenetracion || 0}%</b>
                        </Typography>
                      </Box>
                      <Box mb={3}>
                        <Typography variant="body2" fontWeight="bold" gutterBottom color="error">
                          Antivoto de {simCandidato || "Candidato"}: {simAntivoto}%
                        </Typography>
                        <Slider
                          value={simAntivoto}
                          onChange={(e, val) => setSimAntivoto(val)}
                          valueLabelDisplay="auto"
                          sx={{ color: "#d32f2f" }}
                        />
                        <Typography variant="caption" color="textSecondary">
                          Pregunta 17. Partiendo del Rechazo Real Medido:{" "}
                          <b>
                            {fase2.antivotoCandidatos?.find((c) => c.candidato === simCandidato)?.valor.toFixed(1) || 0}
                            %
                          </b>
                        </Typography>
                      </Box>
                      <Box mb={1}>
                        <Typography variant="body2" fontWeight="bold" gutterBottom color="success.main">
                          Voto Firme (Leal): {simVotoFirme}%
                        </Typography>
                        <Slider
                          value={simVotoFirme}
                          onChange={(e, val) => setSimVotoFirme(val)}
                          valueLabelDisplay="auto"
                          color="success"
                        />
                        <Typography variant="caption" color="textSecondary">
                          Pregunta 16. Intención base de {simCandidato}:{" "}
                          <b>
                            {(() => {
                              let vC = 0
                              let vT = 0
                              data.kpi1.forEach((r) => {
                                vT += Number(r.Cantidad_Votos || 0)
                                if (r.Intencion_Alcalde === simCandidato) vC += Number(r.Cantidad_Votos || 0)
                              })
                              return vT > 0 ? ((vC / vT) * 100).toFixed(1) : 0
                            })()}
                            %
                          </b>
                        </Typography>
                      </Box>
                    </Paper>
                  </Grid>

                  <Grid item xs={12} md={7}>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Paper
                          elevation={3}
                          sx={{
                            p: 3,
                            borderRadius: 3,
                            borderLeft: techoElectoral < 50 ? "6px solid #d32f2f" : "6px solid #196C87",
                          }}
                        >
                          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                            <Typography variant="h6" fontWeight="bold" color="textSecondary">
                              1. Techo Electoral Máximo
                            </Typography>
                            <Tooltip title="Fórmula: 100% - Antivoto. Representa la máxima cantidad de votos posibles a captar.">
                              <InfoIcon color="action" />
                            </Tooltip>
                          </Box>
                          <Typography
                            variant="h3"
                            fontWeight="bold"
                            color={techoElectoral < 50 ? "error" : "primary"}
                            mb={1}
                          >
                            {techoElectoral}%
                          </Typography>
                          <LinearProgress
                            variant="determinate"
                            value={techoElectoral}
                            color={techoElectoral < 50 ? "error" : "primary"}
                            sx={{ height: 10, borderRadius: 5, mb: 2 }}
                          />
                          {techoElectoral < 50 ? (
                            <Alert severity="error" variant="filled" sx={{ borderRadius: 2 }}>
                              <b>ESTADO CRÍTICO:</b> El techo electoral ha perforado el límite de viabilidad del 50%. Es
                              imposible ganar si no se reduce el Antivoto.
                            </Alert>
                          ) : (
                            <Typography variant="body2" color="textSecondary">
                              El candidato es electoralmente viable. Hay terreno para crecer.
                            </Typography>
                          )}
                        </Paper>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Card elevation={3} sx={{ borderRadius: 3, height: "100%" }}>
                          <CardContent>
                            <Box display="flex" justifyContent="space-between">
                              <Typography variant="subtitle2" fontWeight="bold" color="textSecondary">
                                2. ÍNDICE DE PERSUABILIDAD
                              </Typography>
                              <Psychology color="primary" />
                            </Box>
                            <Typography variant="h4" fontWeight="bold" color="primary" my={1}>
                              {persuabilidad}%
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              Fórmula: 100% - Voto Firme. Es la cantidad de electores "blandos" que aún pueden cambiar
                              su decisión.
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Card elevation={3} sx={{ borderRadius: 3, height: "100%" }}>
                          <CardContent>
                            <Box display="flex" justifyContent="space-between">
                              <Typography variant="subtitle2" fontWeight="bold" color="textSecondary">
                                3. EFICIENCIA DE CAMPAÑA
                              </Typography>
                              <Campaign color="secondary" />
                            </Box>
                            <Typography variant="h4" fontWeight="bold" color="secondary" my={1}>
                              {eficienciaCampana}%
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              El canal "{simCanal}" tiene un multiplicador de impacto de x{factorCanal}.
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Box>
            )}

            {/* MENSAGES DE EMPTY STATES PARA TABLAS */}
            {activeTab < 3 &&
              ((activeTab === 0 && data.kpi1.length === 0) ||
                (activeTab === 1 && data.kpi2.length === 0) ||
                (activeTab === 2 && data.kpi3.length === 0)) &&
              !isLoading &&
              !isFetching && (
                <Typography variant="body1" color="textSecondary" align="center" sx={{ py: 10 }}>
                  No hay información detallada disponible para esta vista o no ha finalizado la carga.
                </Typography>
              )}
          </Paper>
        </StyledRoot>
      </div>
    </ThemeProvider>
  )
}

export default EncuestaMantaIndex
